/**
 * Enter-to-send for chat composers. On a desktop keyboard Enter sends and
 * Shift+Enter starts a new line. On touch-first web the on-screen return key
 * starts a new line, like any phone text field, and only the send button
 * sends. Cmd/Ctrl+Enter still sends there, for a tablet with a keyboard.
 */

/** The keyboard hint every chat composer sets: "return", never "send". */
export const COMPOSER_ENTER_KEY_HINT = 'enter';

const COARSE_POINTER_QUERY = '(pointer: coarse)';

/**
 * Whether touch is the primary input, read at keypress time. Same test as
 * useTouchPrimary: a touch screen with a coarse primary pointer, so a laptop
 * with a touch screen and a trackpad still counts as desktop.
 */
export function isTouchPrimaryInput(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
    return false;
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  return hasTouch && window.matchMedia(COARSE_POINTER_QUERY).matches;
}

export type ComposerEnterKeyEvent = {
  key: string;
  shiftKey: boolean;
  metaKey?: boolean;
  ctrlKey?: boolean;
  isComposing?: boolean;
  keyCode?: number;
};

/**
 * Whether this keypress should send the message. False means the composer
 * leaves the key alone, so Enter inserts a new line.
 */
export function shouldSendOnEnter(
  event: ComposerEnterKeyEvent,
  touchPrimary: boolean = isTouchPrimaryInput()
): boolean {
  if (event.key !== 'Enter' && event.key !== 'NumpadEnter') return false;
  // An IME is still composing; this Enter confirms the candidate.
  if (event.isComposing || event.keyCode === 229) return false;
  if (event.shiftKey) return false;
  if (touchPrimary) return Boolean(event.metaKey || event.ctrlKey);
  return true;
}
